import scribe from '@grab-x/scribe-web-sdk';
import _ from 'lodash';
import ScribeEventName, { ScribeConfig } from '../constants/scribe';
import { userTypeToTemplateAppName } from '../data/trident-campaigns';
import { actionTypes } from '../data/trident-types';
import { PORTAL_CONFIG_UPDATED_EVENT } from './portal-config';

/**
 * web scribe sdk initialization
 * src = new uuid4(), as the internal app doesn't specify particular customer (pax, dax, mex)
 * so we leverage it as the browser session id
 */
export const initializeScribe = (token: string) => {
  scribe.initialize(
    ScribeConfig.REGISTERED_NAME,
    _.get(window, 'Grab.config.ENV', 'stg') === 'prd',
  );
  scribe.setAuthToken(token);
  scribe.sendEvent(`${ScribeConfig.SERVICE}.pageloaded`);

  // Defensive check for getVariable method
  if (typeof scribe.getVariable === 'function') {
    scribe.getVariable('commsPortalConfig', {}).then(res => {
      const parsedRes = JSON.parse(res.value);
      window.Grab.config = {
        ...window.Grab.config,
        PORTAL_CONFIG: parsedRes,
      };
      window.dispatchEvent(new Event(PORTAL_CONFIG_UPDATED_EVENT));
    }).catch(err => {
      console.warn('Failed to get commsPortalConfig from scribe:', err);
    });
  }
};

export const sendOpenEvent = (user = '', extraData?: any) => {
  scribe.sendEvent(ScribeEventName.FORM_OPEN, {
    user: getUserName(user),
    ...extraData,
  });
};

export const getUserName = (user = '') => {
  return user.split('@')[0];
};

export const closeTabEvent = (urlMatched: string, callback: (context: any, event: BeforeUnloadEvent) => void) => {
  window.addEventListener('beforeunload', ev => {
    ev.preventDefault();
    if (window.location.href.includes(urlMatched)) {
      callback(this, ev);
    }
  });
};

/**
 * process the given campaign data to an tracking event object.
 * @param entity the campaign entity object
 * @param externalData the external/ custom data that need to be injected into final object
 * @returns tracking event object
 */
export const processCampaignData = (entity: any, externalData: any = {}) => {
  const { nodes, rootNodeScheduleMapping, rootNodeTmGroupMapping } = entity;
  const flattenedActionsData = _.flatten(
    _.map(_.filter(nodes, { type: 'actions' }), x => x.data),
  );

  /**
   * same as the "flattenedActionsData" above too
   * we filter only type of "schedule"/ "scenarios" and then get the treatment id (if any)
   * nodes => map its ID if any => filter only schedule/ scenarios => return Array<{type: string, value: string}>
   * for example:
   * actions: "[{\"type\":\"sendPush\",\"group\":\"comms\",\"value\":\"a3c91ac1-f495-4710-8b05-6704a6663f44\"}]",
   * treatments: "treatments": "[{\"type\":\"scenarios\",\"value\":66863}]",
   */
  const flattenedTmsData = _.map(
    _.filter(
      _.map(_.keys(nodes), key => {
        return {
          ...nodes[key],
          id:
            _.get(rootNodeScheduleMapping, key, null) ||
            _.get(rootNodeTmGroupMapping, key, null),
        };
      }),
      n => {
        return ['schedule', 'scenarios'].includes(n.type);
      },
    ),
    t => {
      return {
        type: t.type,
        value: t.id,
      };
    },
  );

  const actions = _.map(flattenedActionsData, x => {
    const group = _.get(
      _.find(actionTypes, { key: x.type }),
      'trackingGroup',
      '',
    );
    const item = {
      type: x.type,
      group,
      value: null,
    };
    if (group === 'comms') {
      item.value = _.get(x, 'meta.messagingPayload.templateID');
    } else {
      switch (x.type) {
        case 'awardPoint':
          item.value = _.get(x, 'meta.dynamicPoint');
          break;
        case 'awardReward':
        case 'bulkAwardReward':
          item.value = _.get(x, 'meta.rewardID');
          break;
        case 'updateSegMembershipPayload':
        case 'updateDaxGroupMembership':
          item.value = _.get(x, 'meta.updateSegMembershipPayload.SegmentName');
          break;
        case 'updateQuest':
          item.value = ['challengeID', 'incrementType', 'taskID']
            .map(k => _.get(x, `meta.updateQuestPayload.${k}`, ''))
            .join('_');
          break;
        case 'awardSubscription':
          item.value = _.get(x, 'meta.subscriptionPayload.planID');
          break;
        case 'awardNFT':
          item.value = _.get(x, 'meta.awardNFTPayload.web3EventName`');
          break;
        default:
          item.value = '';
      }
    }
    return item;
  });
  const campaignType = _.uniq(_.map(actions, x => x.group))
    .sort()
    .join('_'); // 1.comms 2.trident 3.comms_trident
  const treatmentType = _.uniq(_.map(flattenedTmsData, x => x.type))
    .sort()
    .join('_'); // 1.schedule 2.scenarios 3.schedule_scenarios
  return {
    actions: JSON.stringify(actions),
    treatments: JSON.stringify(flattenedTmsData),
    campaignType,
    treatmentType,
    campaignId: entity.campaignID,
    country: entity.countryID,
    userType: userTypeToTemplateAppName[entity.targetUserType],
    status: entity.status,
    user: getUserName(entity.user.email),
    ...externalData,
  };
};
